/** Webhook endpoints, delivery log and the CloudEvents feed. */
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/app/auth";
import {
  type CloudEvent,
  type Delivery,
  type Endpoint,
  EVENT_CATALOGUE,
  useCreateEndpoint,
  useDeleteEndpoint,
  useDeliveries,
  useEndpoints,
  useEventFeed,
  useRedeliver,
  useRotateSecret,
  useUpdateEndpoint,
} from "@/features/admin/integrations/queries";
import { DataTable } from "@/shared/components/data-table";
import { CompleteDataTable } from "@/shared/components/complete-data-table";
import { TableToolbar } from "@/shared/components/table-toolbar";
import { useListState } from "@/shared/lib/list-state";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader, StatusBadge } from "@/shared/components/page";
import { formatDateTime, formatRelative } from "@/shared/lib/format";
import { useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Textarea } from "@/shared/ui/form";
import { Badge, Dialog, Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/primitives";

interface EndpointValues extends Record<string, unknown> {
  name: string;
  url: string;
  description: string;
  headers: string;
  is_active: boolean;
}

/** Parse "Key: value" lines into a header map. */
export function parseHeaders(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const idx = line.indexOf(":");
    if (idx > 0) {
      out[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
    }
  }
  return out;
}

/** Endpoint create/edit dialog; shows the signing secret once after creation. */
export function EndpointDialog({
  endpoint,
  onClose,
}: {
  endpoint?: Endpoint;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const create = useCreateEndpoint();
  const update = useUpdateEndpoint();
  const [events, setEvents] = useState<string[]>(endpoint?.event_types ?? ["*"]);
  const [secret, setSecret] = useState<string | null>(null);
  const initial = useMemo<EndpointValues>(
    () => ({
      name: endpoint?.name ?? "",
      url: endpoint?.url ?? "",
      description: endpoint?.description ?? "",
      headers: Object.entries(endpoint?.headers ?? {})
        .map(([k, v]) => `${k}: ${v}`)
        .join("\n"),
      is_active: endpoint?.is_active ?? true,
    }),
    [endpoint],
  );
  const form = useForm<EndpointValues>({
    initial,
    schema: z.object({
      name: z.string().trim().min(1, t("app.required")),
      url: z.url(t("webhooks.urlFormat")),
    }),
    idPrefix: "wh",
  });
  const toggle = (ev: string, on: boolean): void => {
    if (ev === "*") {
      setEvents(on ? ["*"] : []);
      return;
    }
    const base = events.filter((x) => x !== "*");
    setEvents(on ? [...base, ev] : base.filter((x) => x !== ev));
  };
  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      const body = {
        name: v.name.trim(),
        url: v.url.trim(),
        event_types: events.length ? events : ["*"],
        headers: parseHeaders(v.headers),
        description: v.description,
        is_active: v.is_active,
      };
      if (endpoint) {
        await update.mutateAsync({ id: endpoint.id, body });
      } else {
        const created = await create.mutateAsync(body);
        setSecret(created.secret);
      }
    });
    if (ok && endpoint) {
      onClose();
    }
  };
  if (secret) {
    return (
      <Dialog
        open
        onOpenChange={(o) => !o && onClose()}
        title={t("webhooks.secretTitle")}
        footer={<Button onClick={onClose}>{t("app.close")}</Button>}
      >
        <p className="text-sm">{t("webhooks.secretHint")}</p>
        <code
          className="block select-all break-all rounded-md bg-muted p-2 font-mono text-xs"
          data-testid="webhook-secret"
        >
          {secret}
        </code>
      </Dialog>
    );
  }
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={endpoint ? t("webhooks.edit") : t("webhooks.new")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
      wide
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="wh-name" label={t("app.name")} error={form.errors.name}>
          <Input {...form.field("name")} />
        </Field>
        <Field id="wh-url" label={t("webhooks.url")} error={form.errors.url}>
          <Input type="url" placeholder="https://" {...form.field("url")} />
        </Field>
      </div>
      <Field id="wh-description" label={t("app.description")}>
        <Input {...form.field("description")} />
      </Field>
      <Field id="wh-headers" label={t("webhooks.headers")} hint={t("webhooks.headersHint")}>
        <Textarea {...form.field("headers")} placeholder="X-Api-Key: …" />
      </Field>
      <div className="space-y-2 rounded-md border p-3">
        <Checkbox
          id="wh-ev-all"
          label={t("notificationRules.allEvents")}
          checked={events.includes("*")}
          onChange={(e) => toggle("*", e.target.checked)}
        />
        {!events.includes("*") ? (
          <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
            {Object.entries(EVENT_CATALOGUE).map(([group, list]) => (
              <fieldset key={group}>
                <legend className="text-xs font-semibold uppercase text-muted-foreground">
                  {group}
                </legend>
                <Checkbox
                  id={`wh-ev-${group}`}
                  label={`${group}.*`}
                  checked={events.includes(`${group}.*`)}
                  onChange={(e) => toggle(`${group}.*`, e.target.checked)}
                />
                {list.map((ev) => (
                  <div key={ev}>
                    <Checkbox
                      id={`wh-ev-${ev}`}
                      label={ev}
                      checked={events.includes(ev)}
                      onChange={(e) => toggle(ev, e.target.checked)}
                    />
                  </div>
                ))}
              </fieldset>
            ))}
          </div>
        ) : null}
      </div>
      <Checkbox
        id="wh-is_active"
        label={t("app.active")}
        checked={form.values.is_active}
        onChange={(e) => form.set("is_active", e.target.checked)}
      />
    </FormDialog>
  );
}

/** Delivery log for one endpoint. */
function DeliveriesPanel({ endpoint }: { endpoint: Endpoint }): ReactElement {
  const { t } = useTranslation();
  const list = useListState(".");
  const status = list.search.status ?? "";
  const paged = useDeliveries(endpoint.id, status || undefined);
  const redeliver = useRedeliver();
  const columns: ColumnDef<Delivery>[] = [
    { id: "at", header: t("app.created"), cell: (c) => formatDateTime(c.row.original.created_at) },
    {
      id: "event",
      header: t("notificationRules.event"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.event_type}</span>,
    },
    {
      id: "status",
      header: t("app.status"),
      cell: (c) => (
        <StatusBadge value={c.row.original.status} namespace="webhooks.deliveryStatus" />
      ),
    },
    {
      id: "attempts",
      header: t("webhooks.attempts"),
      cell: (c) =>
        `${c.row.original.attempts}${c.row.original.response_status ? ` · HTTP ${c.row.original.response_status}` : ""}`,
    },
    {
      id: "error",
      header: t("webhooks.lastError"),
      cell: (c) => (
        <span className="text-xs text-muted-foreground">{c.row.original.last_error || "—"}</span>
      ),
    },
    {
      id: "actions",
      header: t("app.actions"),
      cell: (c) =>
        c.row.original.status !== "success" ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={redeliver.isPending}
            onClick={() => redeliver.mutate(c.row.original.id)}
          >
            {t("webhooks.redeliver")}
          </Button>
        ) : null,
    },
  ];
  return (
    <div className="space-y-2">
      <TableToolbar
        filters={[
          {
            id: "status",
            label: t("app.status"),
            kind: "select",
            prominent: true,
            value: status || undefined,
            options: ["pending", "success", "failed", "exhausted"].map((value) => ({
              value,
              label: t(`webhooks.deliveryStatus.${value}`),
            })),
            onChange: (value) =>
              list.update({ status: typeof value === "string" ? value : undefined }),
            onRemove: () => list.update({ status: undefined }),
          },
        ]}
        onReset={() => list.update({ status: undefined })}
      />
      {paged.query.isPending ? (
        <LoadingState />
      ) : paged.query.isError ? (
        <ErrorState error={paged.query.error} />
      ) : (
        <DataTable
          columns={columns}
          data={paged.items}
          minWidth={900}
          getRowId={(d) => d.id}
          total={paged.total}
          hasMore={paged.hasMore}
          onLoadMore={paged.loadMore}
          isLoadingMore={paged.isLoadingMore}
          emptyMessage={t("webhooks.noDeliveries")}
        />
      )}
    </div>
  );
}

/** Pull-based event feed. */
function EventFeedPanel(): ReactElement {
  const { t } = useTranslation();
  const list = useListState(".");
  const type = list.search.type ?? "";
  const paged = useEventFeed({ type: type || undefined });
  const columns: ColumnDef<CloudEvent>[] = [
    { id: "time", header: t("app.created"), cell: (c) => formatDateTime(c.row.original.time) },
    {
      id: "type",
      header: t("notificationRules.event"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.type}</span>,
    },
    {
      id: "subject",
      header: t("webhooks.subject"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.subject}</span>,
    },
    {
      id: "data",
      header: t("audit.details"),
      cell: (c) => (
        <span className="font-mono text-xs text-muted-foreground">
          {JSON.stringify(c.row.original.data).slice(0, 120)}
        </span>
      ),
    },
  ];
  return (
    <div className="space-y-2">
      <TableToolbar
        filters={[
          {
            id: "type",
            label: t("notificationRules.event"),
            kind: "text",
            value: type || undefined,
            onChange: (value) =>
              list.update({ type: typeof value === "string" ? value : undefined }),
            onRemove: () => list.update({ type: undefined }),
          },
        ]}
        onReset={() => list.update({ type: undefined })}
      />
      {paged.query.isPending ? (
        <LoadingState />
      ) : paged.query.isError ? (
        <ErrorState error={paged.query.error} />
      ) : (
        <DataTable
          columns={columns}
          data={paged.items}
          getRowId={(e) => e.id}
          total={paged.total}
          hasMore={paged.hasMore}
          onLoadMore={paged.loadMore}
          isLoadingMore={paged.isLoadingMore}
        />
      )}
    </div>
  );
}

/** Webhooks page. */
export function WebhooksPage(): ReactElement {
  const { t } = useTranslation();
  const list = useListState(".");
  const { can } = useAuth();
  const endpoints = useEndpoints();
  const remove = useDeleteEndpoint();
  const rotate = useRotateSecret();
  const [dialog, setDialog] = useState<{ endpoint?: Endpoint } | null>(null);
  const [selected, setSelected] = useState<Endpoint | null>(null);
  const manage = can("integrations.webhook.manage");
  const columns: ColumnDef<Endpoint>[] = [
    {
      id: "name",
      header: t("app.name"),
      cell: (c) => (
        <div>
          <div className="font-medium">{c.row.original.name}</div>
          <div className="truncate font-mono text-xs text-muted-foreground">
            {c.row.original.url}
          </div>
        </div>
      ),
    },
    {
      id: "events",
      header: t("notificationRules.event"),
      cell: (c) => (
        <span className="flex flex-wrap gap-1">
          {c.row.original.event_types.slice(0, 4).map((ev) => (
            <Badge key={ev} tone="outline">
              {ev}
            </Badge>
          ))}
          {c.row.original.event_types.length > 4 ? (
            <Badge>+{c.row.original.event_types.length - 4}</Badge>
          ) : null}
        </span>
      ),
    },
    {
      id: "health",
      header: t("webhooks.health"),
      cell: (c) => (
        <span className="flex flex-col text-xs">
          <span className={c.row.original.failure_count ? "text-destructive" : ""}>
            {t("webhooks.failures", { count: c.row.original.failure_count })}
          </span>
          <span className="text-muted-foreground">
            {t("webhooks.lastSuccess")}: {formatRelative(c.row.original.last_success_at)}
          </span>
        </span>
      ),
    },
    {
      id: "status",
      header: t("app.status"),
      cell: (c) =>
        c.row.original.is_active ? (
          <Badge tone="success">{t("app.active")}</Badge>
        ) : (
          <Badge tone="danger">{t("app.inactive")}</Badge>
        ),
    },
    {
      id: "actions",
      header: t("app.actions"),
      cell: (c) => (
        <div className="flex flex-wrap gap-1">
          <Button size="sm" variant="ghost" onClick={() => setSelected(c.row.original)}>
            {t("webhooks.deliveries")}
          </Button>
          {manage ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDialog({ endpoint: c.row.original })}
              >
                {t("app.edit")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={rotate.isPending}
                onClick={() => {
                  if (globalThis.confirm(t("webhooks.rotateConfirm"))) {
                    rotate.mutate(c.row.original.id);
                  }
                }}
              >
                {t("webhooks.rotate")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive"
                onClick={() => {
                  if (globalThis.confirm(t("app.confirmDelete"))) {
                    remove.mutate(c.row.original.id);
                  }
                }}
              >
                {t("app.delete")}
              </Button>
            </>
          ) : null}
        </div>
      ),
    },
  ];
  return (
    <div>
      <PageHeader
        title={t("webhooks.title")}
        subtitle={t("webhooks.subtitle")}
        actions={manage ? <Button onClick={() => setDialog({})}>{t("webhooks.new")}</Button> : null}
      />
      {dialog ? (
        <EndpointDialog endpoint={dialog.endpoint} onClose={() => setDialog(null)} />
      ) : null}
      {rotate.data ? (
        <Dialog
          open
          onOpenChange={() => rotate.reset()}
          title={t("webhooks.secretTitle")}
          footer={<Button onClick={() => rotate.reset()}>{t("app.close")}</Button>}
        >
          <p className="text-sm">{t("webhooks.secretHint")}</p>
          <code
            className="block select-all break-all rounded-md bg-muted p-2 font-mono text-xs"
            data-testid="webhook-secret"
          >
            {rotate.data.secret}
          </code>
        </Dialog>
      ) : null}
      {selected ? (
        <Dialog
          open
          onOpenChange={(o) => !o && setSelected(null)}
          title={`${t("webhooks.deliveries")} · ${selected.name}`}
          wide
        >
          <DeliveriesPanel endpoint={selected} />
        </Dialog>
      ) : null}
      <Tabs
        value={list.search.tab === "feed" ? "feed" : "endpoints"}
        onValueChange={(tab) =>
          list.update({ tab, q: undefined, sort: undefined, status: undefined, type: undefined })
        }
      >
        <TabsList>
          <TabsTrigger value="endpoints">{t("webhooks.endpoints")}</TabsTrigger>
          <TabsTrigger value="feed">{t("webhooks.feed")}</TabsTrigger>
        </TabsList>
        <TabsContent value="endpoints">
          {endpoints.isPending ? (
            <LoadingState />
          ) : endpoints.isError ? (
            <ErrorState error={endpoints.error} onRetry={() => void endpoints.refetch()} />
          ) : (
            <CompleteDataTable
              columns={columns}
              data={endpoints.data}
              getSearchText={(endpoint) => `${endpoint.name} ${endpoint.url}`}
              searchDescription={t("tablePilot.searchFields", {
                fields: `${t("app.name")}, ${t("webhooks.url")}`,
              })}
              sortFields={[
                { value: "name", label: t("app.name"), valueOf: (endpoint) => endpoint.name },
              ]}
              minWidth={950}
              getRowId={(e) => e.id}
              total={endpoints.data.length}
              emptyMessage={t("webhooks.empty")}
            />
          )}
        </TabsContent>
        <TabsContent value="feed">
          <EventFeedPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
